import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { TimeViewEvent } from './time-view.component';

/** MM-DD-YYYY date key format used across timesheets. */
const KEY_FORMAT = 'MM-DD-YYYY';

export interface CalendarCell
{
    date: Date;
    day: number;
    inMonth: boolean;
    isToday: boolean;
    isWeekend: boolean;
}

export interface CalendarDayEvent
{
    /** MM-DD-YYYY formatted date key. */
    keyDate: string;
    value: number;
    comments: string;
}

/** Format a Date as MM-DD-YYYY. */
function toKey(date: Date): string
{
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
}

/** Parse a MM-DD-YYYY key back to a comparable number (YYYYMMDD). */
function keyToNum(key: string): number
{
    const [mm, dd, yyyy] = key.split('-');
    return parseInt(`${yyyy}${mm}${dd}`, 10);
}

function dateToNum(d: Date): number
{
    return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

@Component({
    selector: 'app-timesheet-calendar',
    templateUrl: './timesheet-calendar.component.html',
    styleUrls: ['./timesheet-calendar.component.scss']
})
export class TimesheetCalendarComponent implements OnChanges
{
    /** The month to display. Only year+month matter � day is ignored. */
    @Input() viewDate: Date = new Date();

    /** Start of the allowed range (inclusive). Null = no lower bound. */
    @Input() dateFrom: Date = null;

    /** End of the allowed range (inclusive). Null = no upper bound. */
    @Input() dateTo: Date = null;

    /** When true every cell's time-entry widget is disabled (view-only mode). */
    @Input() readOnly: boolean = false;

    /** Pre-filled hours map: MM-DD-YYYY ? hours */
    @Input() hoursMap: { [dateKey: string]: number } = {};

    /** Pre-filled comments map: MM-DD-YYYY ? comment string */
    @Input() commentsMap: { [dateKey: string]: string } = {};

    /** Fires when the user edits a cell. */
    @Output() dayChanged = new EventEmitter<CalendarDayEvent>();

    /** Fires when the user clicks prev/next month arrows. */
    @Output() viewDateChange = new EventEmitter<Date>();

    readonly dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    cells: CalendarCell[] = [];

    private _builtYear: number = -1;
    private _builtMonth: number = -1;

    ngOnChanges(changes: SimpleChanges): void
    {
        if (changes['viewDate']) { this.buildCells(); }
    }

    cellDateKey(date: Date): string | null
    {
        const key = toKey(date);
        const num = dateToNum(date);

        if (!this.dateFrom && !this.dateTo) { return key; }

        // Ensure dateFrom/dateTo are Date objects (might come in as strings)
        const fromDate = this.dateFrom instanceof Date ? this.dateFrom : (this.dateFrom ? new Date(this.dateFrom) : null);
        const toDate = this.dateTo instanceof Date ? this.dateTo : (this.dateTo ? new Date(this.dateTo) : null);

        const fromNum = fromDate ? dateToNum(fromDate) : null;
        const toNum   = toDate   ? dateToNum(toDate)   : null;

        if (fromNum && toNum) { return (num >= fromNum && num <= toNum) ? key : null; }
        if (fromNum)          { return num >= fromNum ? key : null; }
        return key;
    }

    isFutureCell(date: Date): boolean
    {
        // Block today and future dates (cannot submit hours for today or future)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dateToNum(date) >= dateToNum(today);
    }

    hoursFor(date: Date): number    { return this.hoursMap[toKey(date)] ?? 0; }
    commentsFor(date: Date): string { return this.commentsMap[toKey(date)] ?? ''; }

    onDayChanged(event: TimeViewEvent): void
    {
        this.dayChanged.emit({ keyDate: event.keyDate, value: event.value, comments: event.comments });
    }

    trackByDate(_index: number, cell: CalendarCell): number { return cell.date.getTime(); }

    prevMonth(): void
    {
        const d = new Date(this.viewDate);
        d.setDate(1);
        d.setMonth(d.getMonth() - 1);
        this.viewDateChange.emit(d);
    }

    nextMonth(): void
    {
        const d = new Date(this.viewDate);
        d.setDate(1);
        d.setMonth(d.getMonth() + 1);
        this.viewDateChange.emit(d);
    }

    private buildCells(): void
    {
        const year  = this.viewDate.getFullYear();
        const month = this.viewDate.getMonth();

        if (year === this._builtYear && month === this._builtMonth) { return; }

        const today        = new Date();
        const firstDay     = new Date(year, month, 1).getDay();
        const daysInMonth  = new Date(year, month + 1, 0).getDate();
        const daysInPrev   = new Date(year, month, 0).getDate();

        const cells: CalendarCell[] = [];

        for (let i = firstDay - 1; i >= 0; i--)
        {
            const d = new Date(year, month - 1, daysInPrev - i);
            cells.push({ date: d, day: daysInPrev - i, inMonth: false, isToday: false, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
        }

        for (let d = 1; d <= daysInMonth; d++)
        {
            const date = new Date(year, month, d);
            cells.push({
                date,
                day: d,
                inMonth: true,
                isToday: date.toDateString() === today.toDateString(),
                isWeekend: date.getDay() === 0 || date.getDay() === 6
            });
        }

        const remaining = 42 - cells.length;
        for (let d = 1; d <= remaining; d++)
        {
            const date = new Date(year, month + 1, d);
            cells.push({ date, day: d, inMonth: false, isToday: false, isWeekend: date.getDay() === 0 || date.getDay() === 6 });
        }

        this.cells = cells;
        this._builtYear  = year;
        this._builtMonth = month;
    }
}
